import React, { useState, useEffect } from 'react';
import Modal from '../../../components/Modal';
import AddUserForm, { UserFormData } from './AddUserForm';
import { 
  getUserListAPI, 
  saveUserAPI, 
  deleteUserAPI,
  saveUserModulePermissionAPI,
  User,
  UserSearchRequest,
  UserModulePermission_Request
} from '../../../services/adminApi';
import { ToastContainer } from '../../../components/Toast';
import { useToast } from '../../../hooks/useToast';
import { useModulePermission } from '../../../hooks/useModulePermission';
import styles from './UserManagement.module.css';

const UserManagement: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const { toasts, removeToast, showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const permissions = useModulePermission('Users');
  const [columnPermissions, setColumnPermissions] = useState({
    isView: true,
    isAdd: true,
    isEdit: true,
    isVoid: true,
    isExport: true,
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const searchRequest: UserSearchRequest = {
        searchText: '',
        pageNo: 1,
        pageSize: 100,
      };
      const response = await getUserListAPI(searchRequest);
      if (response.isSuccess && response.data) {
        setUsers(response.data);
      } else {
        showError(response.message || 'Failed to load users');
      }
    } catch (error) {
      console.error('Failed to load users:', error);
      showError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingUser(null);
  };

  const handleSubmit = async (
    formData: UserFormData, 
    permissions?: Map<number, UserModulePermission_Request>
  ) => {
    try {
      console.log('=== UserManagement.handleSubmit ===');
      console.log('Received permissions Map size:', permissions?.size);
      console.log('Received permissions:', permissions);
      
      const userPayload: User & { uPassword?: string } = {
        id: editingUser?.id,
        personName: formData.personName,
        userName: formData.userName,
        userType: formData.userType,
        isActive: formData.status === 'Active' || formData.status === 'active',
        uPassword: formData.password,
      };

      console.log('Saving user with payload:', userPayload);
      const response = await saveUserAPI(userPayload);
      
      console.log('SaveUser response:', response);
      
      if (response.isSuccess) {
        // Get the user ID from response
        // Backend returns: { id: 8, isSuccess: true, message: "...", data: null }
        // So userId is in response.id, NOT in response.data
        const userId = editingUser?.id || (response as any).id;
        
        console.log('User saved successfully. userId:', userId);
        console.log('Response structure:', { 
          responseId: (response as any).id, 
          isSuccess: response.isSuccess,
          responseData: response.data 
        });
        
        // Save user module permissions if provided
        if (permissions && permissions.size > 0 && userId) {
          console.log('Starting to save permissions...');
          console.log('Saving permissions for userId:', userId, 'permissionsCount:', permissions.size);
          
          // Save each permission individually
          const permissionsArray = Array.from(permissions.values());
          console.log('Permissions array:', permissionsArray);
          
          for (const perm of permissionsArray) {
            const permPayload: UserModulePermission_Request = {
              ...perm,
              employeeId: userId,
              isActive: true,
            };
            
            console.log('Saving permission:', permPayload);
            const permResponse = await saveUserModulePermissionAPI(permPayload);
            console.log('Permission save response:', permResponse);
            
            if (!permResponse.isSuccess) {
              console.error('Failed to save permission:', perm.moduleId, 'response:', permResponse);
              showError('User saved but some permissions failed to save');
            } else {
              console.log('Permission saved successfully for moduleId:', perm.moduleId);
            }
          }
        } else {
          console.log('NOT saving permissions. Reason:');
          console.log('  permissions:', permissions);
          console.log('  permissions?.size:', permissions?.size);
          console.log('  userId:', userId);
          console.log('  Check: permissions && permissions.size > 0 =', permissions && permissions.size > 0);
        }

        showSuccess(editingUser ? 'User updated successfully!' : 'User created successfully!');
        await loadData();
        setIsModalOpen(false);
        setEditingUser(null);
      } else {
        showError(response.message || 'Failed to save user');
      }
    } catch (error) {
      console.error('Failed to save user:', error);
      showError('Failed to save user. Please try again.');
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = async (user: User) => {
    if (window.confirm(`Are you sure you want to delete ${user.userName}?`)) {
      try {
        const response = await deleteUserAPI(user.id || 0);
        if (response.isSuccess) {
          await loadData();
          showSuccess('User deleted successfully!');
        } else {
          showError(response.message || 'Failed to delete user');
        }
      } catch (error) {
        console.error('Failed to delete user:', error);
        showError('Failed to delete user. Please try again.');
      }
    }
  };

  const toggleColumnPermission = (permission: keyof typeof columnPermissions) => {
    setColumnPermissions(prev => ({
      ...prev,
      [permission]: !prev[permission],
    }));
  };

  return (
    <div className={styles.container}>
      {/* Toast Container */}
      <ToastContainer toasts={toasts} onRemove={removeToast} />

      <div className={styles.header}>
        <div className={styles.titleSection}>
          <h1 className={styles.title}>User Management</h1>
          <p className={styles.subtitle}>Manage users and their access permissions</p>
        </div>
        {permissions.canAdd && (
          <button className={styles.addButton} onClick={handleAddUser}>
            + Add User
          </button>
        )}
      </div>

      <div className={styles.tableContainer}>
        {loading ? (
          <div className={styles.loading}>Loading users...</div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>USERNAME</th>
                <th>ROLE</th>
                <th>STATUS</th>
                <th>CREATED</th>
                {(permissions.canEdit || permissions.canVoid) && <th>ACTIONS</th>}
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan={permissions.canEdit || permissions.canVoid ? 5 : 4} className={styles.emptyState}>
                    <div className={styles.emptyIcon}>👤</div>
                    <div>No users found</div>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td className={styles.nameCell}>{user.userName}</td>
                    <td>
                      <span className={styles.roleBadge}>{user.userType || 'User'}</span>
                    </td>
                    <td>
                      <span className={`${styles.statusBadge} ${styles[user.isActive ? 'active' : 'inactive']}`}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className={styles.dateCell}>
                      {user.createdDate ? new Date(user.createdDate).toLocaleDateString() : '-'}
                    </td>
                    {(permissions.canEdit || permissions.canVoid) && (
                      <td>
                        <div className={styles.actions}>
                          {permissions.canEdit && (
                            <button
                              className={styles.actionIcon}
                              onClick={() => handleEdit(user)}
                              title="Edit"
                            >
                              ✏️
                            </button>
                          )}
                          {permissions.canVoid && (
                            <button
                              className={`${styles.actionIcon} ${styles.deleteIcon}`}
                              onClick={() => handleDelete(user)}
                              title="Delete"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      <Modal 
        isOpen={isModalOpen} 
        onClose={handleCloseModal} 
        title={editingUser ? 'Edit User' : 'Add New User'}
      >
        <AddUserForm 
          onSubmit={handleSubmit} 
          onCancel={handleCloseModal}
          initialData={editingUser ? {
            id: editingUser.id,
            personName: editingUser.personName,
            userName: editingUser.userName,
            password: '',
            userType: editingUser.userType,
            status: editingUser.isActive ? 'Active' : 'Inactive',
          } : undefined}
          isEditing={!!editingUser}
          userId={editingUser?.id}
        />
      </Modal>
    </div>
  );
};

export default UserManagement;
